import isEmpty from "lodash/isEmpty";
import { cmsSubscriptionService } from "../../../../services/cms-subscription/cms-subscription.service.instance";
import type { ICmsSubscriptionService } from "../../../../services/cms-subscription/cms-subscription.service.interface";
import type { ICmsSubscription } from "../../../../services/cms-subscription/entities/cms-subscription/cms-subscription.entity.interface";
import {
  BONUSX_SUBSCRIPTION_TIER_TO_GENERAL_SUBSCRIPTION_TIER_MAP,
  BONUSX_SUBSCRIPTION_TIERS,
} from "../../../../services/user-subscription/enums/bonusx-subscription-tiers.enum";
import { userSubscriptionService } from "../../../../services/user-subscription/user-subscription.instance";
import type { IUserSubscriptionService } from "../../../../services/user-subscription/user-subscription.service.interface";
import { isSubscriptionTierLowerThanRequested } from "../../../../utils/is-subscription-tier-lower-than-requested/is-subscription-tier-lower-than-requested";
import { ChatbotMessageStreamHandler } from "../../services/chatbot-stream-message-handler/chatbot-stream-message-handler";
import { UserMessageAppendStrategy } from "../../services/chatbot-stream-message-handler/chatbot-stream-message-handler.enum";
import type { IChatbotMessageStreamHandler } from "../../services/chatbot-stream-message-handler/chatbot-stream-message-handler.interface";
import type { ChatbotState } from "../reducer/types/chatbot.reducer.type";
import { ChatbotChatStatusService } from "../services/chatbot-chat-status/chatbot-chat-status.service";
import type {
  IChatbotChatStatusService,
  ShouldBeInEmptyStateProps,
} from "../services/chatbot-chat-status/chatbot-chat-status.service.interface";
import type { IChatbotDispatcherService } from "../services/chatbot-dispatcher/chatbot.dispatcher.service.interface";
import { chatbotSessionService } from "../services/chatbot-session/chatbot-session.service.instance";
import type { IChatbotSessionService } from "../services/chatbot-session/chatbot-session.service.interface";
import type { AskToChatbotParams, IChatbotMediator } from "./chatbot.mediator.interface";

export class ChatbotMediator implements IChatbotMediator {
  constructor(
    private readonly _chatbotDispatcherService: IChatbotDispatcherService,
    private readonly _chatbotMessageStreamHandler: IChatbotMessageStreamHandler = new ChatbotMessageStreamHandler(
      _chatbotDispatcherService
    ),
    private readonly _chatbotSessionService: IChatbotSessionService = chatbotSessionService,
    private readonly _userSubscriptionService: IUserSubscriptionService = userSubscriptionService,
    private readonly _chatbotChatStatusService: IChatbotChatStatusService = new ChatbotChatStatusService(),
    private readonly _cmsSubscriptionService: ICmsSubscriptionService = cmsSubscriptionService
  ) {}

  public sendToAIService(params: AskToChatbotParams): Promise<void> {
    this._chatbotDispatcherService.setErrorMessage(undefined);
    return this._chatbotMessageStreamHandler.processMessage(params);
  }

  public async retryLastMessage(params: AskToChatbotParams): Promise<void> {
    this._chatbotDispatcherService.setErrorWhileGeneratingResponse(false);
    this._chatbotDispatcherService.setErrorMessage(undefined);
    await this._chatbotMessageStreamHandler.processMessage(params, UserMessageAppendStrategy.SKIP_APPEND);
  }

  public toggleEmptyState(props: ShouldBeInEmptyStateProps): void {
    const shouldBeEmptyState = this._chatbotChatStatusService.shouldBeInEmptyState(props);
    const isCorrectlyInEmptyState = this._chatbotChatStatusService.shouldSkipEmptyState(
      shouldBeEmptyState,
      props.isCurrentlyEmptyState
    );

    if (isCorrectlyInEmptyState) {
      return;
    }

    this._chatbotDispatcherService.setShowEmptyState(shouldBeEmptyState);
  }

  public async createNewSession(): Promise<boolean> {
    this._chatbotDispatcherService.setIsCreatingNewSession(true);
    const isSessionCreated = await this._chatbotSessionService.createNewSession();

    if (isSessionCreated) {
      this._chatbotDispatcherService.clearChatMessages();
    }

    this._chatbotDispatcherService.setIsCreatingNewSession(false);
    return isSessionCreated;
  }

  public async canCloneFromPreviousChat(): Promise<boolean> {
    const hasPreviousConversations = await this._chatbotSessionService.hasPreviousConversations();

    if (hasPreviousConversations) {
      const activeSessionMessages = await this._chatbotSessionService.getActiveSessionMessages();
      const hasNoMessagesInActiveSession = isEmpty(activeSessionMessages);

      this._chatbotDispatcherService.setCanCloneFromPreviousChat(hasNoMessagesInActiveSession);
      return hasNoMessagesInActiveSession;
    }

    this._chatbotDispatcherService.setCanCloneFromPreviousChat(false);
    return false;
  }

  public async cloneFromPreviousChat(): Promise<boolean> {
    this._chatbotDispatcherService.setIsCloningFromPreviousChat(true);

    const isClonedSuccessfully = await this._chatbotSessionService.cloneFromPreviousChat();

    if (isClonedSuccessfully) {
      const messages = await this._chatbotSessionService.getActiveSessionMessages();
      this._chatbotDispatcherService.populateChatMessages(messages);
    }

    this._chatbotDispatcherService.setIsCloningFromPreviousChat(false);
    return isClonedSuccessfully;
  }

  public async canUseAiWithCurrentSubscription(): Promise<void> {
    const currentUserSubscriptionTier = await this._userSubscriptionService.getCurrentUserSubscriptionTier();
    const requiredTier = BONUSX_SUBSCRIPTION_TIERS.TIER_2;

    const canUseAiWithCurrentSubscriptionTier = !isSubscriptionTierLowerThanRequested(
      currentUserSubscriptionTier,
      requiredTier
    );

    this._chatbotDispatcherService.setIsAvailableInCurrentSubscription(canUseAiWithCurrentSubscriptionTier);
    this._chatbotDispatcherService.setUserSubscriptionTier(currentUserSubscriptionTier);
  }

  public async checkRequiredSubscriptionForChatbot(): Promise<void> {
    const currentUserSubscription = await this._userSubscriptionService.getSubscriptionAccount();
    const orderedCmsSubscriptions = await this._cmsSubscriptionService.getOrderedSubscriptions();

    const currentUserSubscriptionTier =
      BONUSX_SUBSCRIPTION_TIER_TO_GENERAL_SUBSCRIPTION_TIER_MAP[
        currentUserSubscription.subscriptionTier as BONUSX_SUBSCRIPTION_TIERS
      ];

    const cmsUserSubscription = orderedCmsSubscriptions.find(function checkIfCurrentSubscriptionTierIsInRequiredTiers(
      subscription
    ) {
      return subscription.externalProviderCode === currentUserSubscriptionTier;
    });

    if (!cmsUserSubscription) {
      this._chatbotDispatcherService.setIsAvailableInCurrentSubscription(false);
      this._chatbotDispatcherService.setRequiredTierForChatbot(undefined);
      return;
    }

    this._chatbotDispatcherService.setIsAvailableInCurrentSubscription(cmsUserSubscription.hasAI);
    if (!cmsUserSubscription.hasAI) {
      const nextSubscriptionWithAIEnabled = this._findNextSubscriptionWithAIEnabled(
        orderedCmsSubscriptions,
        cmsUserSubscription
      );
      this._chatbotDispatcherService.setRequiredTierForChatbot(nextSubscriptionWithAIEnabled?.externalProviderCode);
    }
  }

  private _findNextSubscriptionWithAIEnabled(
    orderedCmsSubscriptions: ICmsSubscription[],
    cmsUserSubscription: ICmsSubscription
  ) {
    const orderedTiersWithAIEnabled = orderedCmsSubscriptions.filter(function filterTiersWithAIEnabled(subscription) {
      return subscription.hasAI;
    });

    const nextSubscriptionWithAIEnabled = orderedTiersWithAIEnabled.find(function findNextSubscriptionWithAIEnabled(
      subscription
    ) {
      return subscription.order > cmsUserSubscription.order;
    });
    return nextSubscriptionWithAIEnabled;
  }

  public isChatDisabled(currentState: ChatbotState, isChatbotPanelOpen: boolean): boolean {
    return this._chatbotChatStatusService.isChatDisabled(currentState, isChatbotPanelOpen);
  }

  public setStartStreamingResponse(): void {
    this._chatbotDispatcherService?.setStartStreamingResponse();
  }

  public setEndStreamingResponse(): void {
    this._chatbotDispatcherService?.setEndStreamingResponse();
  }

  public setScrollToBottomAction(value: boolean): void {
    this._chatbotDispatcherService?.setScrollToBottomAction(value);
  }

  public showScrollToBottomButton(): void {
    this._chatbotDispatcherService?.showScrollToBottomButton();
  }

  public hideScrollToBottomButton(): void {
    this._chatbotDispatcherService?.hideScrollToBottomButton();
  }

  public showRetryButton(): void {
    this._chatbotDispatcherService?.setShowRetryButton(true);
  }

  public hideRetryButton(): void {
    this._chatbotDispatcherService?.setShowRetryButton(false);
  }

  public setRetryButtonText(text: string): void {
    this._chatbotDispatcherService?.setRetryButtonText(text);
  }
}
